import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, ShieldCheck, Ban, RotateCcw } from 'lucide-react';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/Card';
import { Pill } from '../../components/ui/Pill';
import { Button } from '../../components/ui/Button';
import { AdminShell } from '../../components/AdminShell';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import { useToast } from '../../components/ui/Toast';
import { formatDate } from '../../lib/format';

interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  company_id: string | null;
  company_name: string | null;
  email_verified: boolean;
  is_disabled: boolean;
  created_at: string;
  last_active_at: string | null;
}

/** Admin → Users. List, search, change role, disable/enable. */
export function AdminUsersPage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const confirm = useConfirm();
  const toast = useToast();

  const users = useQuery<AdminUser[]>({
    queryKey: ['admin-users', q],
    queryFn: async () => (await api.get('/admin/users', { params: { q: q || undefined } })).data,
  });

  const setRole = useMutation({
    mutationFn: async (input: { id: string; role: AdminUser['role'] }) =>
      (await api.patch(`/admin/users/${input.id}`, { role: input.role })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
    onError: () => toast.error(t('admin.users.errorUpdate')),
  });

  const toggleDisabled = useMutation({
    mutationFn: async (input: { id: string; disabled: boolean }) =>
      (await api.patch(`/admin/users/${input.id}`, { is_disabled: input.disabled })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
    onError: () => toast.error(t('admin.users.errorUpdate')),
  });

  async function onToggleDisabled(u: AdminUser) {
    const ok = await confirm({
      title: u.is_disabled ? t('admin.users.confirmEnableTitle') : t('admin.users.confirmDisableTitle'),
      body: u.is_disabled ? t('admin.users.confirmEnableBody', { email: u.email }) : t('admin.users.confirmDisableBody', { email: u.email }),
      confirmLabel: u.is_disabled ? t('admin.users.enable') : t('admin.users.disable'),
      destructive: !u.is_disabled,
    });
    if (!ok) return;
    toggleDisabled.mutate({ id: u.id, disabled: !u.is_disabled });
  }

  return (
    <AdminShell>
      <div className="px-8 py-7 max-w-7xl w-full mx-auto">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-ink-500">{t('admin.eyebrow')}</div>
            <h1 className="display text-3xl font-semibold tracking-tight mt-1">{t('admin.users.title')}</h1>
            <p className="text-sm text-ink-600 dark:text-ink-300 mt-1">{t('admin.users.subtitle')}</p>
          </div>
          <div className="relative w-72">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('admin.users.searchPlaceholder')}
              className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900"
            />
          </div>
        </div>

        <Card className="mt-6 overflow-hidden">
          {users.isLoading ? (
            <div className="p-10 text-center text-ink-500">{t('common.loading')}</div>
          ) : !users.data || users.data.length === 0 ? (
            <div className="p-10 text-center text-ink-500 text-sm">{t('admin.users.empty')}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-ink-50 dark:bg-ink-800/50 text-left">
                  <tr>
                    <th className="px-4 py-2 font-medium">{t('admin.users.col.user')}</th>
                    <th className="px-4 py-2 font-medium">{t('admin.users.col.workspace')}</th>
                    <th className="px-4 py-2 font-medium">{t('admin.users.col.role')}</th>
                    <th className="px-4 py-2 font-medium">{t('admin.users.col.status')}</th>
                    <th className="px-4 py-2 font-medium">{t('admin.users.col.lastActive')}</th>
                    <th className="px-4 py-2 font-medium text-right">{t('admin.users.col.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.data.map((u) => (
                    <tr key={u.id} className="border-t border-ink-200 dark:border-ink-800">
                      <td className="px-4 py-3">
                        <div className="font-medium">{u.name || u.email}</div>
                        <div className="text-xs text-ink-500">{u.email}</div>
                      </td>
                      <td className="px-4 py-3 text-ink-700 dark:text-ink-200">{u.company_name ?? '—'}</td>
                      <td className="px-4 py-3">
                        <select
                          value={u.role}
                          onChange={(e) => setRole.mutate({ id: u.id, role: e.target.value as AdminUser['role'] })}
                          className="text-xs rounded-md border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 px-2 py-1"
                        >
                          <option value="owner">{t('admin.users.roles.owner')}</option>
                          <option value="admin">{t('admin.users.roles.admin')}</option>
                          <option value="member">{t('admin.users.roles.member')}</option>
                          <option value="viewer">{t('admin.users.roles.viewer')}</option>
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        {u.is_disabled ? (
                          <Pill tone="muted">{t('admin.users.disabled')}</Pill>
                        ) : u.email_verified ? (
                          <Pill tone="success">{t('admin.users.verified')}</Pill>
                        ) : (
                          <Pill tone="accent">{t('admin.users.unverified')}</Pill>
                        )}
                      </td>
                      <td className="px-4 py-3 num text-ink-500 text-xs">
                        {u.last_active_at ? formatDate(u.last_active_at, i18n.language) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant={u.is_disabled ? 'secondary' : 'ghost'}
                          size="sm"
                          onClick={() => onToggleDisabled(u)}
                        >
                          {u.is_disabled ? (<><RotateCcw size={14} /> {t('admin.users.enable')}</>) : (<><Ban size={14} /> {t('admin.users.disable')}</>)}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
        <p className="mt-3 text-xs text-ink-500 flex items-center gap-1.5">
          <ShieldCheck size={12} /> {t('admin.users.note')}
        </p>
      </div>
    </AdminShell>
  );
}
